import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import React, {
    useRef
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    PageContent, PageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    Wand2
} from 'lucide-react';
import {
    useNavigate, useLocation
} from 'react-router-dom';
import {
    Card, CardContent
} from 'shared/components/ui/card';

// Define types for TabContent props
interface TabContentProps {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    title: string;
    description: string;
    tags: string[];
    buttonText: string;
    buttonAction: () => void;
}

const TabContent = ({
    icon: Icon, title, description, tags, buttonText, buttonAction
}: TabContentProps) => (
    <Card className="w-full rounded h-full shadow-md bg-background overflow-hidden border">
        <CardContent className="p-0 flex flex-col md:flex-row items-center justify-center h-full w-full">
            <div className="h-full min-w-[200px] w-full md:w-1/3">
                <div className="bg-muted w-full h-full flex items-center justify-center p-6">
                    <Icon className="w-32 h-32 text-[#b71e3d]" />
                </div>
            </div>
            <div className="h-full min-w-[300px] w-full md:w-2/3">
                <div className="px-6 py-4">
                    <div className="font-bold text-xl mb-4 text-center md:text-left">{title}</div>
                    <p className="text-muted-foreground text-sm text-center md:text-left">
                        {description}
                    </p>
                </div>
                <div className="px-6 pt-4 pb-4 text-center md:text-left">
                    {tags.map((tag, index) => (
                        <span key={index} className="inline-block bg-secondary rounded-full px-3 py-1 text-xs font-semibold text-secondary-foreground mr-2 mb-2">
                            {tag}
                        </span>
                    ))}
                </div>
                <div className="px-6 pb-4 flex justify-center md:justify-start">
                    <Button className="w-fit" onClick={buttonAction}>
                        {buttonText}
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);

export function PageBuilderTab() {
    const navigate = useNavigate();
    const location = useLocation();

    const handleNavigate = (page: string) => {
        navigate(`${location.pathname}/${page}`);
    };

    const tabs = [
        {
            value: 'Story Builder',
            label: 'Interactive Story Builder',
            description: 'Optimized for creating dynamic experiences and user-driven narratives.',
            icon: Wand2,
            content: (
                <TabContent
                    icon={Wand2}
                    title="Interactive 3D Design Studio"
                    description="Create captivating and interactive 3D worlds using cutting-edge tools. From conceptualization to execution, bring your most creative visions to life and engage users with fully interactive 3D experiences."
                    tags={['#pageBuilder', '#opcodeBuilder', '#userStory', '#interactiveDesign']}
                    buttonText="Start Building in 3D"
                    buttonAction={() => handleNavigate('page-builder')}
                />
            )
        },
    ];

    const scrollAreaRef = useRef<HTMLDivElement | null>(null);

    return (
        <PageLayout ref={scrollAreaRef}>
            <PageContent>
                <div className="text-center w-full">
                    <div className="pt-4">
                        <Tabs defaultValue={tabs[0].value} className="flex flex-row w-full">
                            <div className="w-1/2 flex flex-col pr-4">
                                <TabsList className="flex flex-col h-auto w-full space-y-2 p-2 bg-transparent">
                                    {tabs.map((tab) => (
                                        <TabsTrigger
                                            key={tab?.value}
                                            value={tab?.value}
                                            className="flex items-center justify-start px-4 py-2 w-full data-[state=active]"
                                        >
                                            <tab.icon className="w-8 h-8 mr-4"/>
                                            <div className="text-left">
                                                <div className="text-sm font-semibold">{tab.label}</div>
                                                <div className="text-xs text-muted-foreground">{tab.description}</div>
                                            </div>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </div>
                            {tabs.map((tab) => (
                                <TabsContent key={tab.value} value={tab.value} className="flex-1 w-1/2">
                                    {tab.content}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                </div>
            </PageContent>
        </PageLayout>
    );
}